import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileSearch, Plus } from "lucide-react";
import { theme, card, emptyFrame, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage, ApiError } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { LoadingBlock } from "../components/Spinner";
import { TierLockModal, TIER_NAMES, type TierErrorPayload } from "../components/TierLockModal";
import { TechnicalScopeForm, type ScopeOptions } from "../components/TechnicalScopeForm";
import { TechnicalScopeDetail, ScopeStatusTag } from "../components/TechnicalScopeDetail";
import { fmtDateOnly } from "../lib/dateOnly";
import { ModalHeader, ModalOverlay, tableHeadRow } from "./resilience/shared";

// Technical scopes (docs/TECHNICAL-SCOPES.md): the written scope for an audit, a test or any other
// work someone carries out on the organisation's systems. The AI drafts from the organisation's own
// records only; the IT reviewers named on a scope confirm the technical detail on My Tasks; a
// confirmed version is issued as the record of what was agreed. ?scope=<id> opens one.
// Tier L2 ("documents") on the API; writes need documents:write or resilience:write.

interface ScopeListRow {
  id: string;
  displayId: number;
  ref: string;
  title: string;
  workType: string;
  workTypeLabel: string;
  status: string;
  statusLabel: string;
  currentVersion: number;
  plannedStart: string | null;
  plannedEnd: string | null;
  performer: string | null;
  issuedAt: string | null;
  updatedAt: string;
  draftError: string | null;
  reviews: { total: number; confirmed: number };
}

export function TechnicalScopesPage() {
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.some((p) => p === "documents:write" || p === "resilience:write");
  const navigate = usePortalNavigate();
  const [params, setParams] = useSearchParams();
  const openId = params.get("scope");
  const [scopes, setScopes] = useState<ScopeListRow[] | null>(null);
  const [options, setOptions] = useState<ScopeOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tierError, setTierError] = useState<TierErrorPayload | null>(null);
  const [locked, setLocked] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.get<{ scopes: ScopeListRow[] }>("/technical-scopes");
      setScopes(d.scopes);
      setError(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 402 && e.code === "tier_locked") {
        setLocked(true);
        setTierError(e.payload as TierErrorPayload);
      } else {
        setError(apiErrorMessage(e, "Could not load the technical scopes"));
      }
      setScopes([]);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    api.get<ScopeOptions>("/technical-scopes/options").then(setOptions).catch(() => { /* the list's own error says enough */ });
  }, []);

  const open = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("scope", id); else next.delete("scope");
    setParams(next);
  };

  const tierModal = tierError && (
    <TierLockModal
      featureName="Technical scopes"
      requiredTierName={TIER_NAMES[tierError.requiredLevel] ?? `tier ${tierError.requiredLevel}`}
      requiredTierLevel={tierError.requiredLevel}
      currentTierLevel={tierError.currentLevel}
      onClose={() => setTierError(null)}
      onRequestUpgrade={() => navigate("/my-services")}
    />
  );

  return (
    <div className="page-pad">
      {tierModal}
      <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
        <FileSearch size={20} /> Technical scopes
      </h1>
      <p style={{ ...pageSubtitle, margin: "0 0 14px", maxWidth: 820 }}>
        The written scope for an audit, a penetration test or any other work someone carries out on your systems:
        what is in and out, who does it, when, and the rules they work to.
      </p>
      <div style={{ ...card, padding: "12px 16px", marginBottom: 20, maxWidth: 820 }}>
        <div style={{ ...text.body }}>
          The AI drafts a scope only from your own records (the assets, network zones, sites, suppliers and standards you
          pick) and writes <strong>[to be confirmed by IT]</strong> wherever it does not know something. The people you send it
          to confirm the technical detail on their My Tasks page. Once every reviewer has confirmed the current version you
          can issue it, and the issued version is kept as the record of what was agreed.
        </div>
      </div>

      {locked ? (
        <div style={{ ...emptyFrame, ...text.body }}>
          Technical scopes come with the Self-Service + Documents tier or above.
          <div style={{ marginTop: 10 }}><button type="button" style={btnPrimary} onClick={() => navigate("/my-services")}>See your services</button></div>
        </div>
      ) : openId ? (
        <TechnicalScopeDetail
          scopeId={openId}
          options={options}
          canEdit={canEdit}
          onBack={() => open(null)}
          onChanged={() => { void load(); }}
        />
      ) : (
        <ScopeList scopes={scopes} error={error} canEdit={canEdit} onOpen={open} onNew={() => setCreating(true)} />
      )}

      {creating && (
        <ModalOverlay wide onClose={() => setCreating(false)}>
          <ModalHeader title="New technical scope" onClose={() => setCreating(false)} />
          {options ? (
            <TechnicalScopeForm
              options={options}
              onCancel={() => setCreating(false)}
              onSaved={(id) => { setCreating(false); void load(); open(id); }}
            />
          ) : (
            <div style={{ padding: 20, ...text.hint }}>Loading what the scope can name…</div>
          )}
        </ModalOverlay>
      )}
    </div>
  );
}

function ScopeList({ scopes, error, canEdit, onOpen, onNew }: {
  scopes: ScopeListRow[] | null;
  error: string | null;
  canEdit: boolean;
  onOpen: (id: string) => void;
  onNew: () => void;
}) {
  if (scopes === null) return <LoadingBlock />;
  const td: React.CSSProperties = { padding: "10px 12px", borderTop: `1px solid ${theme.color.border}`, verticalAlign: "top", ...text.body };

  return (
    <div>
      {error && <div style={{ ...card, padding: 12, marginBottom: 14, ...text.body, color: theme.color.dangerText }}>{error}</div>}
      {canEdit && (
        <div style={{ marginBottom: 14 }}>
          <button type="button" style={btnPrimary} onClick={onNew}><Plus size={14} style={{ verticalAlign: -2, marginRight: 6 }} />New scope</button>
        </div>
      )}
      {scopes.length === 0 ? (
        <div style={{ ...emptyFrame, ...text.body }}>
          <div style={{ ...text.itemTitle, marginBottom: 6 }}>No technical scopes yet</div>
          <div style={{ color: theme.color.muted, maxWidth: 560, margin: "0 auto" }}>
            Before a penetration test, a certification audit or a consultant's work on your systems, write down what it covers.
            {canEdit ? " Start with New scope." : " Someone with document or resilience editing rights can start one."}
          </div>
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={tableHeadRow}>
                <th style={{ padding: "10px 12px", borderBottom: `1px solid ${theme.color.line}` }}>Reference</th>
                <th style={{ padding: "10px 12px", borderBottom: `1px solid ${theme.color.line}` }}>Title</th>
                <th style={{ padding: "10px 12px", borderBottom: `1px solid ${theme.color.line}` }}>Type of work</th>
                <th style={{ padding: "10px 12px", borderBottom: `1px solid ${theme.color.line}` }}>Status</th>
                <th style={{ padding: "10px 12px", borderBottom: `1px solid ${theme.color.line}` }}>Planned</th>
                <th style={{ padding: "10px 12px", borderBottom: `1px solid ${theme.color.line}` }}>IT review</th>
              </tr>
            </thead>
            <tbody>
              {scopes.map((s, i) => (
                <tr key={s.id} onClick={() => onOpen(s.id)} style={{ cursor: "pointer" }}>
                  <td style={{ ...td, borderTop: i ? td.borderTop : "none", ...text.ref, color: theme.color.ref, whiteSpace: "nowrap" }}>{s.ref}</td>
                  <td style={{ ...td, borderTop: i ? td.borderTop : "none" }}>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(s.id); }}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", ...text.itemTitle, fontSize: 13 }}>
                      {s.title}
                    </button>
                    {s.performer && <div style={text.meta}>{s.performer}</div>}
                    {s.draftError && <div style={{ ...text.meta, color: theme.color.dangerText }}>The last draft failed</div>}
                  </td>
                  <td style={{ ...td, borderTop: i ? td.borderTop : "none" }}>{s.workTypeLabel}</td>
                  <td style={{ ...td, borderTop: i ? td.borderTop : "none" }}><ScopeStatusTag status={s.status} label={s.statusLabel} /></td>
                  <td style={{ ...td, borderTop: i ? td.borderTop : "none", whiteSpace: "nowrap" }}>
                    {s.plannedStart || s.plannedEnd
                      ? `${s.plannedStart ? fmtDateOnly(s.plannedStart) : "?"} to ${s.plannedEnd ? fmtDateOnly(s.plannedEnd) : "?"}`
                      : <span style={text.hint}>Not set</span>}
                  </td>
                  <td style={{ ...td, borderTop: i ? td.borderTop : "none", whiteSpace: "nowrap" }}>
                    {s.reviews.total ? `${s.reviews.confirmed} of ${s.reviews.total} confirmed` : <span style={text.hint}>Not sent</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
